import {AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {Router} from '@angular/router';
import {LoaderService, responsiveBreakpoints, ResponsiveService, ToastService} from '@core/src/public-api';
import {AppointmentRequestRequirements} from '@shared/models/appointment-request.model';
import {AppointmentService} from '@shared/services/appointment.service';
import {Subscription} from 'rxjs';
import {appointmentNoticeCategoriesClass, appointmentSlotSearchOptions} from '@constants/appointment.constants';
import {AppConfigService} from '@services/app-config.service';
import {configVariableValues} from '@constants/config-variable-values.constants';
import {LoginService} from '@shared/services/login.service';
import {AppointmentNotice} from '@shared/models/appointment.model';

@Component({
  selector: 'si-appo-appointment-request-requirements',
  templateUrl: './appo-appointment-request-requirements.component.html',
  styleUrls: ['./appo-appointment-request-requirements.component.scss']
})

export class AppoAppointmentRequestRequirementsComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() model = new AppointmentRequestRequirements();
  @Input() readMode: boolean;
  @Input() form: NgForm;
  @Input() formId = 'appoAppointmentRequestRequirementsForm';
  @Input() isRequestTask = false;
  @Input() internalScheduleParamFlag = false; //This parameter is used to filter entities (such as centers, services or professionals) that are available in active schedules of SINA.
  @Input() externalScheduleParamFlag = false; //This parameter is used to filter entities (such as centers, services or professionals) that are available in active schedules of MyCare.
  @Output() formChange = new EventEmitter<NgForm>();
  @ViewChild('appoAppointmentRequestRequirementsForm') appoAppointmentRequestRequirementsForm: NgForm;
  formSubscription: Subscription;
  centerValues = [];
  serviceValues = [];
  professionalValues = [];
  appointmentReasonValues = [];
  isDesktopWidth: boolean;
  subscriptions: Subscription[] = [];
  responsiveBreakPoints = responsiveBreakpoints;
  appointmentSlotSearchOptions = appointmentSlotSearchOptions;
  defaultAppointmentSlotSearch: string;
  wizardStepBackFlag = false;
  isPatient: boolean;
  isLogged: boolean;
  multipleInsuranceAvailable: boolean;
  appointmentNoticeCategoriesClass = appointmentNoticeCategoriesClass;
  guarantorAlerts: AppointmentNotice[] = [];
  appointmentReasonsAlerts: AppointmentNotice[] = [];

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private toastService: ToastService,
    private appointmentService: AppointmentService,
    private responsiveService: ResponsiveService,
    private router: Router,
    private appConfigService: AppConfigService,
    private loginService: LoginService,
    private loaderService: LoaderService
  ) {
    this.isPatient = this.loginService.getIsPatient();
    this.isLogged = this.loginService.isLoggedIn();
    this.multipleInsuranceAvailable = this.appConfigService.hasConfigurationEnabled(configVariableValues.MULTIPLE_INSURANCE_AVAILABLE);
    this.setResponsiveLogic();
  }

  ngOnInit(): void {
    //We check if the center is filled in to know if we are coming from step 2.
    if (this.model.center) {
      this.wizardStepBackFlag = true;
      this.model.searchBy = this.model.searchBy ?? appointmentSlotSearchOptions.CENTER;
    }

    this.getConfigurations();
  }

  ngAfterViewInit(): void {
    //In this case, coming from step2, it is marked as dirty so that the unsaved changes popup appears.
    if (this.wizardStepBackFlag) {
      this.appoAppointmentRequestRequirementsForm.control.markAsDirty();
      this.changeDetectorRef.detectChanges();
      this.getCenters();
      this.getServices();
      this.getProfessionals();
      this.getAppointmentReasons();
    }

    this.setFormSubscription();
  }

  setResponsiveLogic(): void {
    this.subscriptions.push(
      this.responsiveService.getResponsiveStoreObservable().subscribe(responsiveStore => {
        this.isDesktopWidth = responsiveStore.currentWindowWidth >= this.responsiveBreakPoints.MD;
      })
    );
  }

  setFormSubscription(): void {
    this.formSubscription = this.appoAppointmentRequestRequirementsForm.statusChanges.subscribe(() => {
      this.form = this.appoAppointmentRequestRequirementsForm;
      this.formChange.emit(this.appoAppointmentRequestRequirementsForm);
    });
  }

  async getConfigurations(): Promise<void> {
    await this.appConfigService.getConfigurations();
    this.defaultAppointmentSlotSearch = this.appConfigService.getVariableValue(configVariableValues.DEFAULT_APPOINTMENT_SLOT_SEARCH_SCOPE);
    this.multipleInsuranceAvailable = this.appConfigService.hasConfigurationEnabled(configVariableValues.MULTIPLE_INSURANCE_AVAILABLE);
    this.setDefaultDefaultAppointmentSlotSearch();
  }

  onClickButtonRegisterRequest(): void {
    this.appointmentService.setAppointmentRequestRequirementsModel(this.model);
    this.router.navigate(['/request/appointment']);
  }

  selectDefaultLoaded(defaultValue: any, key: string): void {
    this.model[key] = defaultValue;
  }

  onChangeCenter(): void {
    this.model.appointmentReason = [];
    if (this.model.searchBy !== appointmentSlotSearchOptions.PROFESSIONAL) {
      this.model.professional = null;
      this.getProfessionals();
    }

    if (this.model.searchBy !== appointmentSlotSearchOptions.SERVICE) {
      this.getServices();
    }

    this.getAppointmentReasons();
    this.getAppointmentGuarantorAlerts();
    this.getAppointmentReasonAlerts();
  }

  onChangeService(): void {
    this.model.appointmentReason = [];
    if (this.model.searchBy !== appointmentSlotSearchOptions.PROFESSIONAL) {
      this.model.professional = null;
      this.getProfessionals();
    }

    if (this.model.searchBy !== appointmentSlotSearchOptions.CENTER) {
      this.getCenters();
    }

    this.getAppointmentReasons();
    this.getAppointmentGuarantorAlerts();
    this.getAppointmentReasonAlerts();
  }

  onChangeProfessional(): void {
    this.model.appointmentReason = [];
    this.appointmentReasonsAlerts = [];
    if (this.model.searchBy === appointmentSlotSearchOptions.PROFESSIONAL) {
      this.getCenters();
      this.getServices();
    }

    this.getAppointmentReasons();
  }

  onChangeAppointmentReason(): void {
    this.getAppointmentReasonAlerts();
  }

  onChangeCollective(): void {
    this.model.center = null;
    this.model.service = null;
    this.model.professional = null;
    this.model.appointmentReason = [];
    this.getCenters();
    this.getServices();
    this.getProfessionals();
    this.getAppointmentReasons();
    this.getAppointmentGuarantorAlerts();
    this.getAppointmentReasonAlerts();
  }

  async onLoadedOptionsCenterService(optionsLength: number): Promise<void> {
    if (!optionsLength) {
      await this.toastService.warning('SI_NO_CENTER_SERVICE_FOR_INSURANCE_WARNING');
    }
  }

  async onLoadedOptionsReason(optionsLength: number): Promise<void> {
    if (!optionsLength) {
      await this.toastService.warning('SI_NO_REASON_FOR_INSURANCE_WARNING');
    }
  }

  async getCenters(): Promise<void> {
    const serviceId = this.model.searchBy === appointmentSlotSearchOptions.CENTER ? null : this.model.service?.id;
    const professionalId = this.model.searchBy === appointmentSlotSearchOptions.PROFESSIONAL ? this.model.professional?.id : null;
    if (!this.model.collective || !this.model.searchBy) {
      this.model.center = null;
      this.centerValues = [];
      return;
    }

    this.centerValues = await this.appointmentService.getCenters(
      this.model.collective.id,
      serviceId,
      professionalId,
      this.internalScheduleParamFlag,
      this.externalScheduleParamFlag
    );
    this.onLoadedOptionsCenterService(this.centerValues?.length);
  }

  async getServices(): Promise<void> {
    const centerId = this.model.searchBy === appointmentSlotSearchOptions.SERVICE ? null : this.model.center?.id;
    const professionalId = this.model.searchBy === appointmentSlotSearchOptions.PROFESSIONAL ? this.model.professional?.id : null;
    if (!this.model.collective || !this.model.searchBy) {
      this.model.service = null;
      this.serviceValues = [];
      return;
    }

    this.serviceValues = await this.appointmentService.getServices(
      this.model.collective.id,
      centerId,
      professionalId,
      this.internalScheduleParamFlag,
      this.externalScheduleParamFlag
    );
    this.onLoadedOptionsCenterService(this.serviceValues?.length);
  }

  async getProfessionals(): Promise<void> {
    const centerId = this.model.searchBy === appointmentSlotSearchOptions.PROFESSIONAL ? null : this.model.center?.id;
    const serviceId = this.model.searchBy === appointmentSlotSearchOptions.PROFESSIONAL ? null : this.model.service?.id;
    if (
      !this.model.collective
      || !this.model.searchBy
      || (this.model.searchBy !== appointmentSlotSearchOptions.PROFESSIONAL && (!this.model.center || !this.model.service))
    ) {
      this.model.professional = null;
      this.professionalValues = [];
      return;
    }

    this.professionalValues = await this.appointmentService.getProfessionals(
      this.model.collective.id,
      centerId,
      serviceId,
      this.internalScheduleParamFlag,
      this.externalScheduleParamFlag
    );
  }

  async getAppointmentReasons(): Promise<void> {
    if (
      !this.model.collective
      || !this.model.searchBy
      || !this.model.center
      || !this.model.service
    ) {
      this.model.appointmentReason = [];
      this.appointmentReasonValues = [];
      return;
    }

    this.appointmentReasonValues = await this.appointmentService.getAppointmentReasons(
      this.model.collective.id,
      this.model.center.id,
      this.model.service.id,
      this.model.professional?.id,
      this.internalScheduleParamFlag,
      this.externalScheduleParamFlag
    );
    this.onLoadedOptionsReason(this.appointmentReasonValues?.length);
  }

  setDefaultDefaultAppointmentSlotSearch(): void {
    if (this.defaultAppointmentSlotSearch === appointmentSlotSearchOptions.ALL) {
      this.onSelectAppointmentSlotSearchBy(this.model.searchBy);
    } else {
      this.onSelectAppointmentSlotSearchBy(this.defaultAppointmentSlotSearch);
    }
  }

  onSelectAppointmentSlotSearchBy(searchBy: string, isClicked = false): void {
    //If we click searchBy we want to delete those values.
    if (isClicked) {
      this.model.center = null;
      this.model.service = null;
      this.model.professional = null;
      this.model.appointmentReason = [];
      this.model.fromDate = null;
      this.model.timePreference = null;
      this.model.reason = null;
      //If we selected the same option, then we will deselect the searchBy.
      this.model.searchBy = (this.model.searchBy === searchBy) ? null : searchBy;
    } else {
      this.model.searchBy = searchBy;
    }

    this.getSelectorResultsByAppointmentSlotSearchOption(searchBy);
    this.getAppointmentGuarantorAlerts();
    this.getAppointmentReasonAlerts();
  }

  getSelectorResultsByAppointmentSlotSearchOption(filterBy: string): void {
    switch (filterBy) {
      case (appointmentSlotSearchOptions.CENTER):
        this.getCenters();
        break;
      case (appointmentSlotSearchOptions.SERVICE):
        this.getServices();
        break;
      case (appointmentSlotSearchOptions.PROFESSIONAL):
        this.getProfessionals();
        break;
      default:
    }
  }

  async getAppointmentGuarantorAlerts(): Promise<void> {
    if (!this.model.center || !this.model.service || !this.model.collective) {
      this.guarantorAlerts = [];
      return;
    }

    this.loaderService.start();
    try {
      this.guarantorAlerts = await this.appointmentService.getAppointmentAlerts(
        this.model.center?.id,
        this.model.service?.id,
        this.model.collective?.insurance?.id
      );
    } finally {
      this.loaderService.stop();
    }
  }

  async getAppointmentReasonAlerts(): Promise<void> {
    if (!this.model.center || !this.model.service || !this.model.appointmentReason?.length) {
      this.appointmentReasonsAlerts = [];
      return;
    }

    this.loaderService.start();
    try {
      this.appointmentReasonsAlerts = await this.appointmentService.getAppointmentAlerts(
        this.model.center?.id,
        this.model.service?.id,
        null,
        this.model.appointmentReason?.[0]?.id
      );
    } finally {
      this.loaderService.stop();
    }
  }

  ngOnDestroy(): void {
    this.formSubscription.unsubscribe();
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
